import { Link } from 'react-router-dom';
import { FeatureAvailabilityBadge } from '../../shared/FeatureAvailabilityBadge';
import { formatUtcDate } from './formatDate';
import {
  featureAvailability, featuresAddedBy, isUsableFeature, planAboveTier, shortPlanName,
} from './planComparison';
import type { CompanySubscriptionResponse, FeatureSummary, PlanSummary } from './types';

/**
 * What the next plan up adds to the company's current plan, in customer terms. Only what the API listed: features that are built
 * (Operational or Beta) come first as what the plan gives you today; anything not built yet is listed separately and plainly as
 * "Coming soon", never as part of the benefit. Renders nothing when the company is on the highest plan or has no plan.
 *
 * `actionHref` leads to the existing change-plan flow on the billing page (which previews the exact charge before anything is
 * confirmed). This component never changes a plan.
 */
export function NextPlanAdds({
  plans, features, currentTier, actionHref,
}: {
  plans: readonly PlanSummary[];
  features: readonly FeatureSummary[];
  currentTier: number;
  actionHref: string | null;
}) {
  const next = planAboveTier(plans, currentTier);
  const current = plans.find((p) => p.tier === currentTier);
  if (!next || !current) return null;
  const added = featuresAddedBy(features, next);
  const usable = added.filter(isUsableFeature);
  const later = added.filter((f) => !isUsableFeature(f));
  const nextShort = shortPlanName(next.name);
  const currentShort = shortPlanName(current.name);

  return (
    <section className="plan-next-adds" aria-labelledby="plan-next-adds-heading" data-testid="next-plan-adds">
      <h4 id="plan-next-adds-heading">What {nextShort} adds to {currentShort}</h4>
      {usable.length === 0 && (
        <p className="muted">{next.name} does not add anything you can use today beyond {current.name}. Capabilities planned for it are listed below as Coming soon.</p>
      )}
      {usable.length > 0 && (
        <ul aria-label={`Available with ${next.name}`}>
          {usable.map((f) => (
            <li key={f.featureKey}>
              <strong>{f.displayName}</strong> <FeatureAvailabilityBadge readinessStatus={f.readinessStatus} />
              {(f.upgradeMessage ?? f.description) && <span className="feature-matrix__detail">{f.upgradeMessage ?? f.description}</span>}
              {f.implementationNote && <span className="feature-matrix__detail">{f.implementationNote}</span>}
            </li>
          ))}
        </ul>
      )}
      {later.length > 0 && (
        <>
          <p className="muted">Planned for {next.name}, not available yet on any plan:</p>
          <ul aria-label={`Coming soon for ${next.name}`}>
            {later.map((f) => (
              <li key={f.featureKey}>
                {f.displayName} <FeatureAvailabilityBadge readinessStatus={f.readinessStatus} />
                {featureAvailability(f) === 'pilot-only' && <span className="feature-matrix__detail">Available to pilot customers only.</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {actionHref && <Link to={actionHref}>Review the change to {next.name} →</Link>}
    </section>
  );
}

/** A plan change or cancellation already scheduled for the end of the billing period (`pendingPlan*`, `cancelAtPeriodEnd`). */
export function ScheduledPlanChange({
  subscription, plans,
}: {
  subscription: CompanySubscriptionResponse;
  plans: readonly PlanSummary[];
}) {
  if (subscription.pendingPlanCode && subscription.pendingPlanEffectiveUtc) {
    const target = plans.find((p) => p.code === subscription.pendingPlanCode);
    return (
      <p className="plan-scheduled-change" data-testid="scheduled-plan-change">
        <strong>Scheduled change:</strong> your plan changes to {target?.name ?? subscription.pendingPlanCode} on{' '}
        {formatUtcDate(subscription.pendingPlanEffectiveUtc)}. You keep {subscription.planName} until then. You can remove the
        scheduled change on the <Link to="/settings/billing">Billing page</Link>.
      </p>
    );
  }
  if (subscription.cancelAtPeriodEnd) {
    return (
      <p className="plan-scheduled-change" data-testid="scheduled-plan-change">
        <strong>Scheduled cancellation:</strong> this subscription ends on {formatUtcDate(subscription.currentPeriodEndUtc)}. You keep
        full access until then.
      </p>
    );
  }
  return null;
}

/** Who changes the plan and where, in plain words. Entitlement here is the plan; the person's own permission is separate. */
export function PlanProcessNote() {
  return (
    <p className="muted plan-process-note" data-testid="plan-process-note">
      Only your company administrator can change the plan. Plan changes show the exact charge and the date they take effect before you
      confirm, and a downgrade never deletes anything. Billing contact, invoices and payment method are on the{' '}
      <Link to="/settings/billing">Billing page</Link>. Prices are in US dollars, monthly, with no employee limit on any plan.
    </p>
  );
}
